<template>
    <div class="profile" :style="pageStyles">
        <!-- A panel threw while rendering. First branch, so it wins over every
             other state: once a child has failed there is nothing trustworthy
             to draw, and Vue's default is a blank page with the reason only in
             the console. See errorCaptured(). -->
        <div v-if="renderError" class="profile-unavailable box">
            <b-icon icon="alert-triangle" size="is-large" />
            <p class="profile-unavailable-message">Something went wrong loading this profile.</p>
            <!-- Reload rather than a router push: the component tree is in an
                 unknown state, and re-entering the same route would re-run the
                 render that just failed. -->
            <b-button type="is-primary" outlined @click="reloadProfile">Try again</b-button>
        </div>
        <!-- Shown as the whole page rather than as a snackbar over an empty
             profile: there is nothing behind it to look at, and "This user's
             profile is hidden." is an answer, not an error to retry. -->
        <div v-else-if="viewedError" class="profile-unavailable box">
            <b-icon icon="lock" size="is-large" />
            <p class="profile-unavailable-message">{{ viewedError }}</p>
            <!-- Your own profile, not the leaderboard. Someone lands here
                 by following a link to a profile they can't see, so the
                 useful next step is the one page that is definitely theirs —
                 and the guard turns `me` into their real id on the way. -->
            <b-button tag="router-link" to="/profile/me" type="is-primary" outlined>
                Go to your profile
            </b-button>
        </div>
        <!-- Someone else's profile arrives over the network, and `dbInfo` is
             `{}` until it does — which renders as a real-looking profile
             belonging to "Player" at level 0 with no credits, then jumps.
             Only for a profile we're waiting on: your own comes from a store
             that is already populated. -->
        <div v-else-if="viewedLoading && !viewed" class="profile-unavailable box">
            <b-loading :active="true" :is-full-page="false" />
        </div>
        <!-- The Premium upsell, above the profile rather than inside it as a
             card.
             
             Above, because a backlog you are being told about is the reason
             to read the rest of the page — under the fold it was an
             afterthought to something you had already finished looking at.
             
             It is not the player's data — it is us selling something — so
             dressing it in their chosen theme misrepresents whose voice it
             is, and it inherited a different border and sheen on every
             theme. Out here it reads as the page talking, and it keeps one
             appearance for everyone. Matched to the dashboard's width so it
             still lines up with the panel above it. -->
        <router-link
            v-if="!renderError && isOwnProfile && !isPremium && lockedCount > 0"
            to="/premium"
            class="premium-upsell box">
            <span class="premium-upsell-text">
                <span class="premium-upsell-title">
                    {{ lockedCount }} {{ lockedCount === 1 ? 'reward is' : 'rewards are' }} waiting behind Gamebot Premium
                </span>
                <span class="premium-upsell-subtitle">
                    Faster XP, bonus rewards every level, and a crown next to your name.
                </span>
            </span>
            <!-- A styled span, not <b-button>. The whole banner is the link,
                 and putting a button (or another anchor) inside an <a> is
                 invalid HTML — which is why this wanted `tag="span"`, a value
                 b-button rejects: its validator only accepts a, button, input
                 and the router-link family. Bulma's own classes give the same
                 appearance without the component. -->
            <span class="button is-warning">See what you get</span>
        </router-link>

        <main v-if="!renderError && !viewedError" class="dashboard-content columns box">
            <aside class="sidebar column">
                <b-menu-list>
                    <div class="p-1">
                        <b-menu>
                        <b-menu-list
                        type="is-primary"
                        label="PROFILE">
                            <!-- Router links, not panel switches. Each tab is
                                 a real URL, so it can be linked to and the
                                 back button steps between them.

                                 `:to` carries the id forward rather than
                                 hardcoding /profile/me — these have to keep
                                 working while looking at someone else. -->
                            <b-menu-item
                                tag="router-link"
                                :to="tabTo('ProfileOverview')"
                                :active="$route.name === 'ProfileOverview'"
                                icon="user"
                                label="Overview"></b-menu-item>
                            <!-- Statistics and Achievements are one tab. The
                                 numbers alone are half a dozen figures, not
                                 enough to be worth a click, and the catalog
                                 is what gives them somewhere to go. It also
                                 leaves a slot for SERVERS, below. -->
                            <b-menu-item
                                tag="router-link"
                                :to="tabTo('ProfileStats')"
                                :active="$route.name === 'ProfileStats'"
                                icon="chart-line"
                                label="Stats"></b-menu-item>
                            <!-- Only for your own profile, and only a tab —
                                 this used to be a pen in the corner of the
                                 profile card opening a modal, which hid the
                                 one screen that sells the shop behind an icon
                                 with no label. -->
                            <b-menu-item
                                v-if="isOwnProfile"
                                tag="router-link"
                                :to="tabTo('ProfileCustomize')"
                                :active="$route.name === 'ProfileCustomize'"
                                icon="pen"
                                label="Customize"></b-menu-item>
                            <!-- Leaves the profile entirely, which is why it
                                 is never `active`. On a phone the premium
                                 banner sits below the whole reward track —
                                 this is the short way there.

                                 A subscriber has already made the decision
                                 this item is selling, and managing an active
                                 subscription lives in the account dropdown
                                 now — so it's gone entirely rather than
                                 pointing back at the sales page. -->
                            <b-menu-item
                                v-if="!isPremium"
                                class="is-premium-link"
                                tag="router-link"
                                to="/premium"
                                icon="star"
                                label="Premium"></b-menu-item>
                        </b-menu-list>
                        </b-menu>
                    </div>
                </b-menu-list>
            </aside>

            <!-- One panel per route. `keep-alive` is what lets the Overview
                 panel keep the reward track's scroll position across a trip
                 to Stats and back — it used to survive because the panel was
                 only ever v-show'd. -->
            <keep-alive>
                <router-view
                    :theme="currentTheme"
                    :db-info="dbInfo"
                    :interactive="!isViewingOther"
                    @show-locked="premiumDetails = $event" />
            </keep-alive>

            <!-- What a locked reward actually is, and how to get it. -->
            <b-modal :active="!!premiumDetails" :width="420" @close="premiumDetails = null">
                <div v-if="premiumDetails" class="premium-modal box">
                    <p class="premium-modal-eyebrow">Premium reward</p>
                    <h1 class="premium-modal-title">Level {{ premiumDetails.level }}</h1>
                    <p class="premium-modal-reward">{{ premiumDetails.label }}</p>
                    <p class="premium-modal-body">
                        This one needs Gamebot Premium. Subscribe and it's claimable straight
                        away — along with every premium reward you've already passed.
                    </p>
                    <b-button
                        tag="router-link"
                        to="/premium"
                        type="is-primary"
                        expanded>
                        See what you get
                    </b-button>
                    <button type="button" class="premium-modal-dismiss" @click="premiumDetails = null">
                        Not now
                    </button>
                </div>
            </b-modal>

            <!-- Rewards claimed. The celebration owns the whole moment —
                 count-up, burst, and a per-reward reveal — because a
                 claim is the payoff the entire track is building to. -->
            <claim-celebration
                :active="!!lastClaim"
                :rewards="celebrationRewards"
                @close="dismissClaim" />
        </main>

    </div>
</template>

<style lang="scss">
$dashboard-width: $widescreen;
$user-info-row-height: 2.5rem;
// Fixed rather than proportional: the avatar is an identifier, not a
// feature of the page, and it shouldn't grow with the card.
$avatar-size: 4rem;
// Shared so the XP fill can derive its inner radius from the bar's outer one.
$xp-bar-radius: 0.5rem;

.glass { 
    background-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(30px);
    -webkit-transform: translateZ(0);
    -moz-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
}

.profile {
    text-align: left;
    background-color: #eee;
    min-height: 700px;
    padding: 150px 20px;

    // Edge to edge on a phone. The side padding and the card's rounding are
    // desktop framing; on a narrow screen they only cost usable width, and
    // the reward track in particular wants every pixel it can get.
    @include mobile {
        padding: 110px 0 0 0;

        .dashboard-content {
            border-radius: 0;
            margin-left: 0;
            margin-right: 0;
            max-width: none;
        }
    }
    flex-direction: column;
    align-items: center;

    // Stands in for the whole dashboard when the profile can't be shown.
    // Sized to roughly where the profile card would have been, so the page
    // doesn't collapse to a strip of text against the themed background.
    .profile-unavailable {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        max-width: 26rem;
        // A floor rather than a height, so the loading state and the refusal
        // occupy the same space and one doesn't resize into the other.
        min-height: 14rem;
        margin: 4rem auto;
        padding: 2.5rem 2rem;
        text-align: center;
        // <b-loading> positions against the nearest positioned ancestor.
        position: relative;
    }

    .profile-unavailable-message {
        font-size: 1.1rem;
        font-weight: bold;
        // The copy is ours and short, but a long unbroken string must break
        // rather than push the panel wider than the viewport.
        overflow-wrap: anywhere;
    }

    // The Premium upsell, above the dashboard and matched to its width.
    // Deliberately untouched by the theme: it is the page speaking, not the
    // player's profile, so it looks the same for everyone.
    .premium-upsell {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
        width: 100%;
        max-width: $dashboard-width;
        margin: 0 auto 1.25rem;
        padding: 1.1rem 1.4rem;
        text-decoration: none;
        color: inherit;
        transition: filter 0.15s ease;

        &:hover { filter: brightness(0.98); }

        // Edge to edge on a phone, matching .dashboard-content above it.
        @include mobile {
            border-radius: 0;
            margin-left: 0;
            margin-right: 0;
            max-width: none;
        }
    }

    .premium-upsell-text {
        display: block;
        text-align: left;
    }

    .premium-upsell-title {
        display: block;
        font-weight: bold;
        font-size: 1.05rem;
    }

    .premium-upsell-subtitle {
        display: block;
        font-size: 0.9rem;
        opacity: 0.7;
    }

    // The route out to the sales page, and the modal behind a locked card.
    // Gold, because that's Premium's colour everywhere else in the product.
    .premium-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
        background: linear-gradient(105deg, $gold-light, $gold);
        color: #3a3320;
        border-radius: 1rem;
        text-decoration: none;
        transition: filter 0.15s ease;

        &:hover { filter: brightness(1.03); }
    }

    .premium-banner-title {
        font-weight: bold;
        font-size: 1.05rem;
    }

    .premium-banner-subtitle {
        font-size: 0.85rem;
        opacity: 0.75;
    }

    .premium-banner-cta {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        font-weight: bold;
        white-space: nowrap;
    }

    .dashboard-content {
        position: relative;
        max-width: $dashboard-width;
        padding: 0;
        overflow: hidden;
        margin: 0 auto;

        // Must match the breakpoint the sidebar becomes a tab row at
        // (`touch`, below). Stacking at `mobile` left a 769–1023px band where
        // the options rendered as horizontal tabs but the row was still a row,
        // so the tab strip sat beside the profile instead of above it.
        @include touch {
            display: block;
        }

        // Greyscale throughout, and identical at every width — the vertical
        // list on desktop and the tab row on touch differ only in layout.
        //
        // Light rather than the docs page's $primary fill: that pairing
        // (#F1FCFD on #52D9E9) is light-on-light at roughly 1.6:1 against a
        // 4.5 minimum. Greyscale also lets the reward track's colour be the
        // only colour in this view.
        .sidebar {
            max-width: 250px;

            .menu-label {
                color: $grey;
                padding-left: 0.75rem;
            }

            // Gold, because that's Premium's colour everywhere else in the
            // product. Scoped through the <li>: b-menu-item puts a `class` on
            // its root element, which is the list item, not the anchor.
            .menu-list .is-premium-link a {
                color: $gold-dark;
                font-weight: bold;

                &:hover {
                    color: $gold-dark;
                    background-color: rgba($gold, 0.18);
                }
            }

            .menu-list a {
                color: $text;
                border-radius: 999px;

                &:hover {
                    background-color: $background;
                    color: $text;
                }

                &.is-active {
                    // A step darker than the hover fill above, so the two
                    // stay distinguishable — hover is $background (#f5f5f5)
                    // and this is $grey-lighter (#dbdbdb). Text is
                    // $grey-darker rather than $dark, which in this project
                    // is rgb(35, 39, 48) and carries a blue cast.
                    background-color: $grey-lighter;
                    color: $grey-darker;

                    &:hover {
                        background-color: $grey-lighter;
                        color: $grey-darker;
                    }
                }
            }

            // Mobile and tablet: a row of bubbles above the content rather
            // than a column beside it, which would eat a third of a narrow
            // screen before any profile appeared.
            @include touch {
                max-width: none;
                width: 100%;

                .menu-label { display: none; }

                // Buefy nests two .menu-lists with wrapper divs between them.
                // Flexing the outer one turned those wrappers into shrink-to
                // -fit flex items, so the inner list — the one that actually
                // holds the tabs — only ever had ~200px of a 375px strip to
                // divide between three items.
                .p-1,
                .menu {
                    width: 100%;
                }

                .menu-list {
                    display: flex;
                    gap: 0.5rem;

                    li {
                        flex: 1 1 0;
                        min-width: 0;
                    }

                    a {
                        // Buefy's anchor is inline-level; without this the
                        // bubbles bunched to the left of the strip.
                        width: 100%;
                        // Icon above the label. Side by side, "Achievements"
                        // ran out of room in a third of a phone screen and
                        // wrapped, dropping that tab out of line.
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 0.25rem;
                        font-size: 0.7rem;
                        white-space: nowrap;
                        text-align: center;
                        padding: 0.6rem 0.25rem;
                        border-radius: 1rem;

                        // Buefy spaces the icon for a horizontal item; it sits
                        // centred above the label here.
                        .icon {
                            margin-right: 0;
                            margin-left: 0;
                        }
                    }
                }
            }
        }

        .guild-icon {
            width: 24px;
            height: 24px;
            border-radius: 100%;
            margin-right: 5px;
            overflow: hidden;
        }
        .guild-name {
            line-height: 24px;
            vertical-align: top;
        }
    }

    a.inline-link {
        padding: 0;
        display: inline;
        margin: 0;

        &:hover {
            background-color: transparent;
        }
    }
}

.user-dashboard {
    background-size: cover;
    // Positioning context for the sheen and the tier-styled cards. No
    // overflow:hidden here — see .theme-scene for why that moved.
    position: relative;
    background-color: #eee;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    // Flex items default to min-width:auto and refuse to shrink below their
    // content. Once the reward track carried all 50 levels instead of 5, that
    // pushed this column (and the dashboard with it) to several times the
    // viewport width, and the track never scrolled because it was never
    // narrower than its contents.
    min-width: 0;

    // Everything real sits above the scene. Excludes .modal: that's Buefy's
    // own wrapper for <claim-celebration>, <profile-customizer> and the
    // premium-details modal, which are declared here as plain children so
    // the components stay simple, but Bulma's .modal is position:fixed to
    // cover the whole viewport — forcing it to position:relative like every
    // other child pinned it to this panel's box instead, so the "overlay"
    // never got past the profile section.
    > *:not(.theme-scene):not(.modal) {
        position: relative;
        z-index: 1;
    }
}

// ── The themed box ───────────────────────────────────────────────────────
//
// Legendary reshapes the card; mythical puts architecture on top of it. The
// wrapper exists to anchor that: the header hangs off its top edge, which
// can't be positioned against the card itself once the card is clipped.

.user-profile-wrap {
    position: relative;

    &.has-header { margin-top: var(--header-height, 0); }
}

.box-frame {
    // Unstyled unless the card is clipped — see boxFrameStyles().
    position: relative;
}

.box-header {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    // A plain box rather than the <svg> itself: an SVG is a replaced element,
    // so its width resolves from its own aspect ratio and left/right can't
    // stretch it — the architecture ended up sitting in the corner at its
    // natural size. The box stretches; the svg fills the box.
    > svg {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
    }

    // The full-width part, behind the centred motif.
    .box-header-rail {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
    }
    // Overlaps the card's top edge by a hair so no seam shows between the
    // architecture and the box it sits on.
    margin-bottom: -1px;
    pointer-events: none;
    display: block;
}

// The card's surface, over the overlay fill and under the contents.
.box-material {
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: inherit;
    pointer-events: none;
}

.user-profile > *:not(.box-material) {
    position: relative;
    z-index: 1;
}

.dashboard-item {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    border-radius: 1rem;
    padding: 1rem;
    margin: 0;
    // Positioning context for anything anchored to a card's corner — the
    // edit affordance on the profile card, for one.
    position: relative;
}

// Escalates the Overview cards past what the theme's colour alone carries.
// Epic adds a border in the theme's own accent (read from --tier-accent,
// set alongside overlayStyles — the value differs per theme, so it isn't
// duplicated into these rules). Legendary adds a sheen sweeping across the
// card; mythical doubles the border into a frame. Common and rare are
// deliberately untouched.
.dashboard-item.glass.tier-epic,
.dashboard-item.glass.tier-legendary,
.dashboard-item.glass.tier-mythical {
    // Published so children can correct their own corners against it — an
    // element sitting inside the border needs a *smaller* radius than the
    // border's outer one, or its corners bulge through. See .current-progress.
    --tier-border: 1px;
    border: 1px solid var(--tier-accent, transparent);
}

.dashboard-item.glass.tier-legendary,
.dashboard-item.glass.tier-mythical {
    // border-radius: inherit on the pseudo clips it to the card's rounded
    // corners without overflow:hidden on the card itself — the profile card
    // holds the "Most Played" tooltips, and overflow:hidden here would trap
    // them against the card the same way it used to trap them against the
    // whole panel (see .theme-scene above).
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        // A wide, faint wash rather than a bright stripe. At 0.35 alpha over
        // a 16%-wide band this was a hard glare sweeping the card — it read
        // as a highlight moving *across* the surface instead of light
        // falling on it. Spread over most of the width at a third the
        // strength, it's the same idea without the flashbulb.
        background: linear-gradient(
            115deg,
            transparent 12%,
            rgba(255, 255, 255, 0.06) 34%,
            rgba(255, 255, 255, 0.12) 50%,
            rgba(255, 255, 255, 0.06) 66%,
            transparent 88%
        );
        background-size: 260% 100%;
        animation: card-sheen 11s ease-in-out infinite;
        pointer-events: none;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
            display: none;
        }
    }
}

.dashboard-item.glass.tier-mythical {
    --tier-border: 2px;
    border-width: 2px;
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: -6px;
}

@keyframes card-sheen {
    0%, 15% { background-position: 120% 0; }
    65%, 100% { background-position: -120% 0; }
}

.user-experience {
    padding: 0;
    flex-direction: column;
    // Same reason as .user-dashboard — this is the direct parent of the
    // scrolling track, so it has to be allowed to be narrower than it.
    min-width: 0;
}

.user-dashboard > *, .user-experience > * {
    // margin-top: 1rem;
}

.user-info {
    display: flex;
    // Takes the room the statistics don't need, rather than a fixed half.
    // At 50% the name and pills had ~367px of a 934px card while "MOST
    // PLAYED" and "WINS" held 226px each to show "No games yet" and "0" —
    // so the pill row started dropping achievements on a card with a third
    // of its width unused.
    flex: 1 1 auto;
    min-width: 300px;
    align-items: center;
    justify-content: flex-start;
    gap: 1.25rem;

    .user-info-text {
        // The gap above owns the spacing now; this only needs to not crowd
        // the card's own padding.
        padding: 0.25rem 0;
        min-width: 0;
        // Take the space left beside the avatar rather than sizing to
        // content. Without this the column shrank to roughly the width of the
        // pill row itself, which is exactly the width at which it wraps.
        flex: 1 1 auto;
    }

    .user-username {
        font-size: 1.5rem;
        margin: 0;
        align-items: center;
        gap: 0.5rem;
        font-weight: bold;
    }

    .user-info-tags {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem;
        .tag {
            display: inline-flex;
            justify-content: left;
        }
    }
}

.user-info-row {
    height: $user-info-row-height;
    overflow: hidden;
    margin: 0;
}

.user-statistic {
    text-align: center;
    // Sized to its content instead of growing. These are a short label over a
    // small number; the space is worth more to the name beside them. The
    // min-width is the widest label ("MOST PLAYED") so it can't wrap.
    flex: 0 0 auto;
    min-width: 7.5rem;
    // Column with the value pinned to the bottom of its own row, so the two
    // labels underneath sit on one line. They were previously in differently
    // shaped stacks — "MOST PLAYED" under a row of circles, "WINS" beside a
    // number with the season under it — and never lined up.
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    .statistic-title {
        font-size: 1.8rem;
        line-height: $user-info-row-height;
        margin: 0;
        font-weight: bold;
    }

    // The shared label under each statistic. Lighter than the value it
    // describes — it's the caption, not the number.
    .statistic-label {
        font-size: 0.8rem;
        text-transform: uppercase;
        font-weight: bold;
        line-height: 100%;
        padding: 0.35rem 0;
        margin: 0;
        opacity: 0.55;
    }
}

.game-circles {
    display: block;

    .circle {
        height: $user-info-row-height;
        width: $user-info-row-height;
        
        display: inline-flex;
        margin: 0 0.25rem;
        font-size: 0.8rem;
        font-weight: bold;
        color: white;
    }
}
// Scoped for the same reason as .avatar — `.circle` also belongs to the
// leaderboard and the WebUI drawing component.
.game-circles .circle {
    flex-grow: 1;
    aspect-ratio: 1;
    border-radius: 50%;
    // Overridden inline per game — this is only the fallback for a stats key
    // with no colour assigned.
    background-color: hsl(0, 0%, 73%);

    /* Center Text For Most Played Games */
    display: flex;
    align-items: center;
    justify-content: center;
}

.xp-progress-bar {
    position: relative;
    min-height: 3rem;
    width: 100%;
    border-radius: $xp-bar-radius;
}

.current-progress {
    position: absolute;
    // Stretched between the edges rather than given a height. It used to
    // inherit the card's `min-height`, which is measured on the border box
    // while an absolutely positioned child is laid out in the padding box —
    // so on every tier that adds a border the fill was 2–4px taller than the
    // space it sits in, and spilled over the frame.
    left: 0;
    top: 0;
    bottom: 0;
    // Not `inherit`: that copies the bar's *outer* radius, while the fill sits
    // inside the border. Matching the outer curve on an inner box makes the
    // corners bulge past the frame — the inner radius is the outer one less
    // the border it sits behind.
    border-radius: calc(#{$xp-bar-radius} - var(--tier-border, 0px));
    // Solid, not a gradient — the finale card is the one place on this page
    // that carries one. The colour itself is applied inline from the theme's
    // secondary, the same value the quest progress bars use, so the bar
    // belongs to whatever background is applied.
}

.xp-progress-label {
    position: absolute;
    top: 50%;
    // Inset from both edges. This was `left: 1rem; width: 100%`, which is a
    // full rem wider than its container — the label ran past the end of the
    // bar at every width, and on a phone the text is long enough to notice.
    left: 1rem;
    right: 1rem;
    transform: translateY(-50%);
    text-transform: uppercase;
    // One line: the bar has a fixed height, so wrapping would overflow it
    // vertically rather than reflow.
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @include mobile {
        font-size: 0.75rem;
        left: 0.75rem;
        right: 0.75rem;
    }
    // No colour of its own — it inherits the theme's text colour from the
    // card, which is the value already chosen to read against both the track
    // and the fill. Hardcoding it broke on the dark themes.
}

.user-status {
    margin: 0;
    padding: 0;
    gap: 1rem;
    align-items: stretch;
}

// Achievements

.user-achievements {
    display: flex;
    flex-grow: 10;
    // Column, so the reset tab can sit along the bottom edge rather than
    // taking a whole column to itself beside the quests.
    flex-direction: column;
    gap: 0.75rem;
    // The panel holds three quests now, not the two the mockup showed.
    padding-bottom: 0.5rem;
}

.achievement-list {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
}

.achievement-timer {
    align-self: center;
    font-size: 0.7rem;
    line-height: 1;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    opacity: 0.7;
    // Faint pill so it reads as a footnote to the quest list, not a stat.
    background-color: rgba(0, 0, 0, 0.06);
}

.achievement {
    width: 100%;
    // The name and its progress sit together as one phrase. Flex rather than
    // inline text so the counter keeps a consistent gap regardless of how
    // long the quest name is.
    .achievement-title {
        display: flex;
        align-items: baseline;
        gap: 0.4rem;
        font-size: 0.8rem;
        line-height: 1;
        font-weight: bold;
        margin: 0;
        min-width: 0;
    }

    // Name + progress on the left, reward on the right, all one line.
    .achievement-info {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        font-size: 0.8rem;
        margin: 0 0 0.3rem 0;
        justify-content: space-between;
    }

    .achievement-meta {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        margin: 0;
        flex-shrink: 0;
    }

    // Reward and progress share one muted treatment — the quest name is the
    // only thing at full weight.
    .achievement-reward,
    .achievement-progress-label {
        font-size: 0.75rem;
        opacity: 0.6;
        line-height: 1;
    }

    .achievement-done {
        color: $success;
        font-weight: bold;
        line-height: 1;
    }


    // The unfilled track. Was Bulma's $light (#f5f5f5), which is invisible
    // against a white card — so only the filled portion read as a bar, with
    // nothing to show how far it had to go. A translucent neutral instead:
    // it darkens a light theme and lightens a dark one, without needing a
    // per-theme value.
    .achievement-progress {
        position: relative;
        width: 100%;
        height: 0.5rem;
        border-radius: 0.5rem;
        background-color: rgba(127, 127, 127, 0.3);
        overflow: hidden;

        .achievement-progress-bar {
            position: absolute;
            left: 0;
            top: 0;
            height: inherit;
            border-radius: inherit;
            // Both of these are overridden inline — the width from the
            // quest's progress, the colour from the theme's `accent`. Accent
            // rather than `secondary` (which the XP bar uses) because these
            // bars carry no text: the XP bar needs a fill its label stays
            // readable on, whereas these only need to stand apart from the
            // track, and secondary sat too close to it on the light themes.
            // Zero rather than a placeholder percentage, so a bar that loses
            // its binding reads as "no progress" instead of inventing some.
            width: 0;
            background-color: $primary;
            transition: width 0.3s ease;
        }
    }
}

// Balance

.user-balance {
    display: flex;
    align-items: center;
    flex-grow: 1;
    gap: 0.25rem;

    .user-balance-content {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .user-balance-currency {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8rem;
        font-weight: bold;
        margin: 0;
    }

    .currency-icon {
        width: 2rem;
        height: 2rem;
    }

    .user-balance-amount {
        display: flex;
        font-size: 0.8rem;
        line-height: 2rem;
    }
}

// Rewards

.rewards-container {
    display: flex;
    position: relative;
    width: 100%;
    gap: 0.75rem;

    // Native swipe instead of a carousel dependency: scroll-snap gives touch
    // momentum and snapping for free, works in every browser we support, and
    // adds nothing to the bundle. On desktop all five cards fit, so this
    // never actually scrolls there.
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-padding: 0 0.75rem;

    // Vertical room for the cards' own glows.
    //
    // `overflow-x: auto` cannot leave the other axis visible — CSS computes
    // overflow-y to `auto` alongside it — so this box clips top and bottom
    // whether or not it ever scrolls vertically. The finale's 26px glow was
    // being sliced off flat against that edge. Overflow clips at the padding
    // box, so padding is what buys the glow somewhere to land.
    padding: 0.9rem 0 0.75rem;

    // The scrollbar would sit right under the cards and read as a divider.
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }

    .reward-container {
        // Sized for 5.4 cards rather than exactly 5, so the sixth peeks in at
        // the edge. Five cards that fit perfectly read as the whole list —
        // the sliver is what says the track keeps going.
        flex: 0 0 calc((100% - 3rem) / 5.4);
        min-width: 0;

        // On a phone that formula gives ~47px cards, which is too small to
        // read an icon and a number in. A fixed smaller size instead: the
        // track is already scrollable, so fewer visible at a legible size
        // beats more at an illegible one.
        @include mobile {
            flex: 0 0 4.75rem;
        }
        text-align: center;
        scroll-snap-align: center;
        // Column flex so a card without an aspect-ratio (the finale) can fill
        // whatever height the square cards establish for the row, instead of
        // trying to resolve a percentage height against the wrong axis.
        display: flex;
        flex-direction: column;
    }

    .reward-label {
        font-size: 1rem;
        text-transform: uppercase;
        font-weight: bold;
        line-height: 100%;
        padding: 0.35rem 0;
        margin: 0.5rem 0;

        // Scaled with the cards, which are 4.75rem here rather than a fifth
        // of the row.
        @include mobile {
            font-size: 0.8rem;
            margin: 0.35rem 0;
        }
    }

    .reward {
        width: 100%;
        // Square, always — the one exception is the finale below, which is
        // deliberately wider. aspect-ratio alone let a card with a Claim
        // button grow taller than its neighbours, so the contents scale to
        // fit rather than the box growing to hold them.
        aspect-ratio: 1;
        // Now a direct child of .reward-container: neither grow nor shrink,
        // which the (removed) tooltip wrapper used to hold for it. The
        // finale overrides this to fill the row's height.
        flex: 0 0 auto;
        border-radius: 0.75rem;
        z-index: 1;
        // Anchors the lock badge.
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        padding: 0.5rem;
        text-align: center;
        overflow: hidden;
    }

    .reward-icon {
        width: 2rem;
        height: 2rem;
        object-fit: contain;
    }

    .reward-value {
        font-size: 0.8rem;
        font-weight: bold;
        margin: 0;
        line-height: 1.1;
    }

    // The brand's crown, not a padlock. A lock says "you can't have this";
    // the crown says what it belongs to, which is the same information
    // pointed at the thing we'd rather they do something about.
    .reward-crown-badge {
        position: absolute;
        top: 0.4rem;
        right: 0.5rem;
        width: 0.85rem;
        height: auto;
        line-height: 1;
        // Lifts it off the tessellated scrim behind it.
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55));
    }

    .finale-crown {
        width: 0.7rem;
        height: auto;
        vertical-align: -0.1em;
        margin-right: 0.15rem;
    }

    .reward-lock {
        font-size: 0.7rem;
        line-height: 1.2;
        color: $warning;
        font-weight: bold;
    }

    // The reward itself stays visible under the scrim — dimmed enough to read
    // as unavailable, legible enough to still be an argument for subscribing.
    .reward-container.locked {
        .reward-icon { opacity: 0.65; }
        .reward-value { color: white; opacity: 0.8; }
    }

    // The marks that stand in for the level number on a phone.
    //
    // They take their own spacing rather than the number's: .reward-label is
    // sized for a line of text (padding + margin + uppercase leading), which
    // around a 13px glyph reads as a cramped icon floating too far from the
    // card it belongs to. Pulled up tight underneath, and given a little more
    // room inside its own box so the shape isn't touching the edges.
    .reward-label.is-claim-mark,
    .reward-label.is-claimed-mark {
        display: flex;
        align-items: center;
        justify-content: center;
        // 0.875rem = exactly 14px, inside Buefy's 16px is-small box. The size
        // is deliberately a whole number: FontAwesome draws these from a
        // 512-unit viewBox with the artwork running to the edge, so at a
        // fractional size (0.9rem resolved to 14.4px, landing on x.56/x.96)
        // the outermost row falls on a half device-pixel and rasterises away
        // — which reads as the glyph being cropped by a pixel.
        font-size: 0.875rem;
        line-height: 1;
        padding: 0;
        margin: 0.2rem 0 0;
    }

    .reward-label.is-claimed-mark {
        color: $success;
        // Claimed is history, not an instruction — it recedes the way the
        // dimmed card behind it does.
        opacity: 0.55;
    }

    .reward-label.is-claim-mark {
        color: $success;

        &.is-working { animation: claim-mark-pulse 0.9s ease-in-out infinite; }
    }

    .reward-claimed {
        font-size: 1rem;
        color: $success;
        font-weight: bold;
        line-height: 1;
    }

    .reward-claim {
        // Buefy's own min-height would push a square card out of shape, so
        // the size is set here rather than by a `size` prop. Bigger than it
        // was: this is the one thing on the track anyone is meant to press,
        // and it was smaller than the level number underneath it.
        height: auto;
        padding: 0.3rem 0.85rem;
        font-size: 0.82rem;
        border-radius: 999px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    }

    // The claimable one is the call to action — it gets the colour, and
    // everything else recedes so it reads at a glance.
    .reward-container.claimable .reward {
        outline: 2px solid $success;
    }

    // The player's current level anchors the track. Tinting the number was
    // too quiet — a filled badge reads at a glance. Its colours come from the
    // theme inline, so it belongs to the applied background rather than
    // assuming a light one.
    .reward-container.is-current .reward-label {
        border-radius: 999px;
        width: 1.9rem;
        height: 1.9rem;
        line-height: 1.9rem;
        margin: 0.5rem auto;
        padding: 0;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);

        @include mobile {
            width: 1.5rem;
            height: 1.5rem;
            line-height: 1.5rem;
            margin: 0.35rem auto;
        }
    }

    // ── Textures for what you can't have yet ──────────────────────────────
    //
    // A locked card and a not-yet-reached card were both just "the normal
    // card, dimmer", which makes the track read as one flat wash of grey
    // between the few cards that are live. A tessellated pattern gives each
    // state a surface of its own that survives being dimmed, and it costs
    // nothing — repeating gradients, no image request.
    //
    // Both are pitched *under* the reward: the icon and its value are the
    // argument for subscribing, and a texture that competes with them is a
    // texture turned up too far.

    // Premium: a gold cross-hatch, woven, on the dark scrim. Gold because
    // that is Premium's colour everywhere else in the product.
    .reward-container.locked .reward {
        background-color: rgba(0, 0, 0, 0.72);
        background-image:
            repeating-linear-gradient(45deg, rgba($gold, 0.11) 0 2px, transparent 2px 9px),
            repeating-linear-gradient(-45deg, rgba($gold, 0.08) 0 2px, transparent 2px 9px);
        color: white;
    }

    // Not yet reached: a neutral dot grid. Quieter than the cross-hatch on
    // purpose — this one isn't selling anything, it's just not your turn.
    .reward-container.future .reward,
    .reward-container.unknown .reward {
        background-image: radial-gradient(circle, rgba(0, 0, 0, 0.11) 1px, transparent 1.3px);
        background-size: 9px 9px;
    }

    .reward-container.claimed .reward,
    .reward-container.future .reward,
    .reward-container.unknown .reward {
        opacity: 0.55;
    }

    // ── The season finale (level 50) ──────────────────────────────────────
    //
    // The chase goal, so it earns the only card on the track that breaks the
    // grid: double width, and the only one that moves. Everything else on the
    // page is deliberately still, which is what lets this read as special
    // rather than as noise.

    .reward-container.is-finale {
        // Two cards wide plus the gap they'd have had between them.
        flex-basis: calc(((100% - 3rem) / 5.4) * 2 + 0.75rem);

        // The neighbouring cards are a fixed 4.75rem here rather than a
        // fraction of the row, so this has to be stated in the same terms —
        // the percentage formula above would leave the finale narrower than
        // two of them.
        @include mobile {
            flex-basis: calc(4.75rem * 2 + 0.75rem);

            // Wider, not taller. Its contents are intrinsically ~118px tall,
            // which at this card size stretched the whole row and left every
            // square card floating in a too-tall slot. Scaled to fit the same
            // height its neighbours set.
            .finale-label { display: none; }

            .finale-rewards { gap: 0.6rem; }

            .finale-reward {
                grid-template-rows: 1.75rem 0.85rem 0.7rem;
                row-gap: 0.1rem;
            }

            .reward-icon.is-large {
                width: 1.75rem;
                height: 1.75rem;
            }

            .reward-value { font-size: 0.65rem; }

            .finale-premium-tag {
                font-size: 0.5rem;
                min-height: 0.7rem;
                gap: 0.1rem;
            }
        }

        .reward {
            // No longer square — twice as wide as its neighbours, and as tall
            // as whatever height they set for the row.
            aspect-ratio: auto;
            flex: 1 1 auto;
            // Mythical pink drifting through Gamebot's own blue. Animating
            // background-position over an oversized gradient is the same
            // trick the shop already uses on mythical items (@keyframes
            // gradient, styles.scss) — it composites on the GPU and doesn't
            // touch layout, so it's cheap enough to leave running.
            background: linear-gradient(
                125deg,
                #2b1b3d 0%,
                #5a2470 20%,
                #a3358f 42%,
                #2f7f96 66%,
                #43265c 86%,
                #2b1b3d 100%
            );
            // Wider than the shop's 250%: a bigger gradient means each colour
            // occupies more of the card at once, so the drift reads as a slow
            // shift in mood rather than bands sweeping past.
            background-size: 400% 400%;
            color: white;
            border: 2px solid $rarity-mythical;
            animation:
                gradient 22s ease-in-out infinite alternate,
                finale-glow 5s ease-in-out infinite alternate;
        }

        .reward-value { color: white; }
        .reward-label { color: $rarity-mythical; font-weight: bold; }

        // Even locked, the finale keeps its treatment — the flat scrim used
        // elsewhere would kill the one card meant to be worth chasing.
        &.locked .reward { opacity: 1; background-color: transparent; }
        &.claimed .reward,
        &.future .reward,
        &.unknown .reward { opacity: 0.85; }
    }

    .finale-label {
        font-size: 0.6rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: bold;
        color: $rarity-mythical;
        margin: 0;
    }

    .finale-rewards {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
    }

    // Fixed rows rather than a plain column, so the two rewards line up with
    // each other instead of each centring its own differently-tall stack:
    // icons share a row, labels share a row, and the premium row is reserved
    // on both (empty for the credits) so "1,290" and "Mythical Item" sit on
    // the same line.
    .finale-reward {
        display: grid;
        grid-template-rows: 2.75rem 1.1rem 0.85rem;
        justify-items: center;
        align-items: center;
        row-gap: 0.15rem;

        // Dimmed, not hidden — a free player should still see the Mythical
        // item sitting there, just clearly not theirs yet.
        &.is-premium-locked {
            .reward-icon { opacity: 0.55; filter: none; }
            .reward-value { opacity: 0.65; }
        }
    }

    .finale-reward-icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .finale-premium-tag {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.15rem;
        font-size: 0.6rem;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: $warning;
        margin: 0;
        // Reserved even when empty, so it can't push one label off the line.
        min-height: 0.85rem;
    }

    // Sits between the two rewards, aligned to the icon row rather than the
    // whole stack — the labels and the premium tag below would otherwise drag
    // it off centre.
    .finale-separator {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background-color: $primary;
        box-shadow: 0 0 6px rgba(82, 217, 233, 0.9);
        // The grid's first row is the icons; match its height and centre in it.
        align-self: start;
        margin-top: calc(2.75rem / 2 - 2.5px);
        animation: finale-pulse 2s ease-in-out infinite;
    }

    .reward-icon.is-large {
        width: 2.75rem;
        height: 2.75rem;
        filter: drop-shadow(0 0 6px rgba(243, 82, 181, 0.8));
    }

    // Three twinkling points, offset so they never blink in unison.
    .finale-sparkles {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        pointer-events: none;
        background-image:
            radial-gradient(circle, #fff 0 1.5px, transparent 2px),
            radial-gradient(circle, #fff 0 1px, transparent 1.5px),
            radial-gradient(circle, #fff 0 1.5px, transparent 2px);
        background-repeat: no-repeat;
        // Each layer needs its own small box. Without an explicit size every
        // gradient fills the whole element and draws its dot at that box's
        // centre, so background-position has nothing to move and all three
        // sparkles stack in the middle of the card — directly on top of the
        // separator dot.
        background-size: 10px 10px, 8px 8px, 10px 10px;
        // Away from the centre — that space belongs to the separator dot.
        background-position: 10% 22%, 90% 26%, 80% 80%;
        animation: finale-sparkle 2.4s ease-in-out infinite;
    }
}

@keyframes finale-glow {
    from {
        box-shadow:
            0 0 14px rgba(243, 82, 181, 0.45),
            inset 0 0 20px rgba(243, 82, 181, 0.2);
    }
    to {
        box-shadow:
            0 0 26px rgba(243, 82, 181, 0.75),
            inset 0 0 30px rgba(243, 82, 181, 0.35);
    }
}

@keyframes claim-mark-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
}

@keyframes finale-sparkle {
    0%, 100% { opacity: 0.25; }
    40% { opacity: 1; }
    70% { opacity: 0.5; }
}

@keyframes finale-pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 0.55;
    }
    50% {
        transform: scale(1.5);
        opacity: 1;
    }
}

// Respect a reader who has asked the OS for less movement. The finale still
// reads as special through colour and size with nothing animating.
@media (prefers-reduced-motion: reduce) {
    .rewards-container .reward-container.is-finale .reward {
        animation: none;
        box-shadow: 0 0 20px rgba(243, 82, 181, 0.6);
    }
    .rewards-container .finale-sparkles {
        animation: none;
        opacity: 0.6;
    }
    .rewards-container .finale-separator {
        animation: none;
        opacity: 1;
    }
}

.rewards-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0.75rem;

    .rewards-title {
        font-size: 0.8rem;
        text-transform: uppercase;
        font-weight: bold;
        margin: 0 0.6rem 0 0;
    }

    // Everything after the season pill is pushed to the right.
    .season-pill {
        margin-right: auto;
    }

    // Deliberately quiet: it's a convenience for someone who has scrolled
    // away, not something to draw the eye while they're reading the track.
    .rewards-recentre {
        margin-right: 0.5rem;
        background-color: transparent;
        border-color: transparent;
        opacity: 0.5;
        transition: opacity 0.15s ease;

        &:hover,
        &:focus {
            opacity: 1;
            background-color: rgba(127, 127, 127, 0.12);
            border-color: transparent;
        }

        // Still laid out, just not shown or reachable — removing it from
        // layout made the whole header jump sideways whenever it appeared.
        // The label carries the level, so the reserved width grows with it
        // rather than being fixed.
        &.is-hidden-until-needed {
            visibility: hidden;
            opacity: 0;
            pointer-events: none;
        }
    }
}

.user-footer {
    padding: 0.25rem 1rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    // Caption left, share right. `.dashboard-item` already supplies the
    // flex row and centring; this only decides how the space between them
    // is spent.
    justify-content: space-between;
    gap: 0.75rem;
}

.user-footer-caption {
    // The theme's display face can be tall or wide; this keeps the row the
    // same height whichever theme is applied.
    line-height: 1.2;
    min-width: 0;
}

// A link, drawn as a quiet affordance rather than a button — the footer is
// the calmest part of the card and a filled control would own it.
.share-profile {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    flex: 0 0 auto;
    padding: 0.15rem 0.5rem;
    border: 0;
    border-radius: 0.6rem;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
    // Inherited from the card, so it stays legible on every theme.
    opacity: 0.75;
    transition: opacity 0.12s ease, background-color 0.12s ease;

    &:hover,
    &:focus-visible {
        opacity: 1;
        background-color: rgba(0, 0, 0, 0.08);
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 1px;
    }

    // Holds its ground once copied, so the confirmation doesn't read as a
    // hover state that happens to be stuck.
    &.is-copied { opacity: 1; }

    @media (prefers-reduced-motion: reduce) { transition: none; }
}

// Avatar
//
// Nested under .user-info rather than left bare. This stylesheet is global —
// the profile's route chunk stays in the document after you navigate away —
// and `.avatar` is a name four other components use: the Discord mockups on
// the home page and /docs, the leaderboard, and the nav. A top-level
// `.avatar` here reached all of them the moment anyone opened a profile.
// `flex: 0 0 4rem` was the damaging part: it sets a flex-basis none of those
// components declare, so their avatars snapped to 4rem in a flex row whatever
// width they'd asked for. Nothing looked wrong until you had been to /profile
// first, which is the worst way for a bug to present itself.
.user-info {
    .avatar {
        width: $avatar-size;
        height: $avatar-size;
        border-radius: 50%;
        object-fit: cover;
        // Neither grow nor shrink — a flex item with `auto` basis would
        // otherwise take its size from the row.
        flex: 0 0 $avatar-size;
    }

    // Shown when there's no avatar to load. Deliberately not the shared
    // `.circle` class, which carries `flex-grow: 1` for the most-played row
    // and stretched this to fill whatever space was going.
    .avatar-placeholder {
        background-color: rgba(0, 0, 0, 0.15);
    }
}

// The season, as a pill beside the "Season rewards" heading.
.season-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.75rem;
    // A pill by default, but seasonPillStyles overrides the radius inline on
    // any theme whose box has a shape of its own — a stadium chip floating on
    // Blockworld's stepped, hard-cornered panel was the one round thing left.
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1;
    white-space: nowrap;
}

// The claim buttons, wearing the theme's accent.
//
// The colour arrives inline (claimButtonStyles), which beats every stylesheet
// rule including Bulma's `:hover` — so hover can't be a colour swap here and
// is a brightness shift instead. `.button` is doubled up in the selector to
// out-specify Bulma's own `.button:hover` border, which would otherwise draw
// its default grey ring around a themed fill.
.themed-claim.button {
    border-color: transparent;
    font-weight: bold;
    transition: filter 0.15s ease;

    &:hover,
    &:focus {
        border-color: transparent;
        filter: brightness(1.08);
    }

    &:active { filter: brightness(0.94); }

    // Keyboard users get the ring back — brightness alone is too quiet to
    // serve as a focus indicator.
    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
}

.premium-crown {
    display: inline-block;
    // Sized off the heading rather than fixed, so it scales with the username
    // beside it instead of drifting out of proportion at other breakpoints.
    width: 0.85em;
    height: auto;
    vertical-align: baseline;
    // Darkened rather than left at the source gold — no shadow underneath it
    // now, so the crown needs to hold its own contrast against a light
    // custom background instead of leaning on a shadow to separate it.
    filter: brightness(0.72);
}

// Titles — the pill takes its colour from the achievement's rarity tier.
//
// Selector mirrors Bulma's own `.tag:not(body)`, which is specificity (0,2,0)
// and was out-ranking a plain `.title-tag` — the filled pills were rendering
// Bulma's default dark-grey text on a saturated rarity colour instead of the
// white intended here.
.tag.title-tag:not(body) {
    font-weight: bold;
    color: white;

    // The fill itself comes from the global `.is-<tier>` rules in
    // assets/styles/variables.scss — the same tier gradients the shop uses.
    // Nothing is redeclared here: those are gradients, so a `background-color`
    // in this block would sit *under* the gradient and never be seen.
    //
    // Mythical is the one exception, and it has to use the shorthand to
    // replace the global's image rather than paint beneath it.
    &.is-mythical {
        background: linear-gradient(102.34deg, $rarity-mythical 49.62%, #33ceff 109.32%);
    }
    &.is-empty {
        background-color: transparent;
        border: 1px dashed currentColor;
        color: inherit;
        opacity: 0.6;
    }

    // The overflow badge. Neutral rather than any rarity's colour — it stands
    // for a mixed set, so borrowing one tier's colour would misrepresent it.
    &.is-more {
        background-color: #fff;
        color: #6b6b6b;
        border: 1.5px solid currentColor;
        font-size: 0.7rem;
        height: auto;
        padding: 0.25rem 0.5rem;
        // It navigates, so it has to look like it does.
        cursor: pointer;
        transition: color 0.15s ease, background-color 0.15s ease;

        &:hover,
        &:focus {
            color: $link;
            background-color: rgba(127, 127, 127, 0.08);
        }
    }

    // Everything that isn't the equipped title. Outlined rather than filled,
    // white rather than the tier's colour: same information, visibly a rung
    // below, so the chosen title still reads as chosen.
    &.is-outlined {
        background-color: #fff;
        border: 1.5px solid currentColor;
        font-weight: bold;
        // A shade smaller than the equipped title, reinforcing the same
        // hierarchy the outline already sets up.
        font-size: 0.7rem;
        height: auto;
        padding: 0.25rem 0.6rem;

        // Colour only. The white above is enough now that the global tier
        // rules skip `.is-outlined` — each of these used to repeat
        // `background-color: #fff` and it never worked, because the global
        // gradient was an image sitting on top of it.
        &.is-common    { color: $rarity-common; }
        &.is-rare      { color: $rarity-rare; }
        &.is-epic      { color: $rarity-epic; }
        &.is-legendary { color: $rarity-legendary; }
        &.is-mythical  {
            // Still the shorthand: the filled `&.is-mythical` above is a
            // local gradient that `:not(.is-outlined)` can't reach.
            background: #fff;
            color: $rarity-mythical;
        }
    }
}

.premium-modal {
    text-align: center;

    .premium-modal-eyebrow {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: bold;
        color: $gold-dark;
    }

    .premium-modal-title {
        font-size: 1.75rem;
        font-weight: 800;
        line-height: 1.1;
    }

    .premium-modal-reward {
        font-weight: bold;
        margin-bottom: 0.75rem;
    }

    .premium-modal-body {
        font-size: 0.9rem;
        opacity: 0.75;
        margin-bottom: 1.25rem;
    }

    .premium-modal-dismiss {
        display: block;
        margin: 0.75rem auto 0;
        background: none;
        border: 0;
        font-size: 0.85rem;
        opacity: 0.6;
        cursor: pointer;
        font-weight: bold;
    }
}

// A locked card is the one thing on the track you can interact with.
.reward.is-clickable { cursor: pointer; }

.statistic-empty {
    font-size: 0.8rem;
    opacity: 0.6;
    line-height: $user-info-row-height;
}

// Premium upsell

.premium-upsell {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    border-radius: 1rem;
    border: 2px solid $gold;

    .premium-upsell-title {
        font-weight: bold;
        margin: 0;
    }

    .premium-upsell-subtitle {
        font-size: 0.85rem;
        margin: 0;
        opacity: 0.8;
    }
}

</style>


<script>
import { mapGetters, mapState } from 'vuex'

import { reportRenderError } from '../util/renderErrors.js'

import { themes } from '../globals/profiles.js'
import { pageStyles, loadThemeFont } from '../util/themeStyles.js'
import {
    describeReward,
    rewardAmount,
    rewardIcon,
    rewardKind,
    rewardUnit,
    gemRarity } from '../util/rewardDisplay.js'

import ClaimCelebration from '@/components/Profile/ClaimCelebration.vue'

/**
 * The profile shell: who this page is about, and the frame around whichever
 * panel the router has put in it.
 *
 * ── What lives here and what doesn't ─────────────────────────────────────
 *
 * This owns exactly three things: resolving the subject (whose profile,
 * their data, their theme), the chrome around the panels (the page frame,
 * the tab rail, the hidden/loading states), and the two modals.
 *
 * The panels themselves are routes — `/profile/:id`, `/stats`, `/customize`
 * — so each owns its own markup and its own state, and the reward track no
 * longer has to share a file with the theme picker.
 *
 * The modals are here rather than in the panel that triggers them for two
 * reasons. A modal rendered inside a routed panel is destroyed the moment
 * you change tabs, mid-animation. And Buefy's `.modal` is `position: fixed`
 * against the viewport, so nesting it inside a panel that manages its own
 * overflow is how it ended up trapped inside the profile once already.
 *
 * The celebration needs nothing from the panel at all — it reads `lastClaim`
 * from the store, which is the only thing that can set it. Only the
 * premium-details modal takes an event.
 */
export default {
    name: 'Profile',
    components: { ClaimCelebration },
    data() {
        return {
            // Set by errorCaptured() when a panel throws while rendering.
            renderError: null,
            // The locked reward whose details are open, or null. Set from the
            // Overview panel's `show-locked`.
            premiumDetails: null,
        }
    },
    computed: {
        ...mapGetters('profile', ['lockedCount']),
        ...mapState('profile', ['premium', 'lastClaim', 'viewed', 'viewedError', 'viewedLoading']),

        /** The signed-in user's id, or null when nobody is signed in. */
        ownID() { return this.$store.getters.getUser.id || null },

        /**
         * Whose profile this is. `/profile/me` and `/profile/:ownId` are the
         * same page; anything else is someone else's, which is read-only.
         *
         * Null on `/profile/me` while logged out — there is no such user to
         * resolve, which is a third case and not an error.
         */
        userID() {
            const id = this.$route.params.id
            return (!id || id === 'me') ? this.ownID : id
        },

        /**
         * Gates the Customize tab, and everything the panels treat as an
         * action rather than a display.
         *
         * Requires an actual id on both sides. Comparing them directly said
         * `null === null` on `/profile/me` while logged out, which made a
         * visitor with no account the owner of the empty profile they were
         * looking at — and offered them a Customize tab whose every write
         * would 401.
         *
         * Hiding those is a courtesy, not the enforcement — no endpoint
         * accepts a target other than its authenticated caller, so this is
         * about not offering an action that isn't yours, not about
         * preventing one.
         */
        isOwnProfile() {
            return this.ownID !== null && this.userID === this.ownID
        },

        /**
         * Viewing a *named other user* — the read-only case.
         *
         * The `!!this.userID` half is now belt and braces. It was there for a
         * third state: the signed-out shell at `/profile/me`, belonging to
         * nobody, which kept the reward track and the season because those
         * are the same for every visitor and `!isOwnProfile` alone took them
         * away. Profiles are signed-in only now (router/profileGuard.js) and
         * `me` is rewritten to a real id before this renders, so `userID` is
         * always set and there are two states again.
         *
         * Kept rather than removed because it costs nothing and the
         * alternative — this quietly becoming `!isOwnProfile` — is what the
         * shell was introduced to stop. If the gate is ever relaxed, this is
         * the line that decides what a signed-out visitor sees.
         */
        isViewingOther() {
            return !!this.userID && !this.isOwnProfile
        },

        /**
         * The user this page is about, in one shape, with their display name
         * and avatar folded in.
         *
         * Own profile reads the root store, which is live — it moves as you
         * claim a reward or change a theme. Anyone else's is the snapshot
         * `GET /api/profile/:id` returned. Every panel reads *this* and never
         * the store, so nothing downstream has to know whose profile it is
         * rendering.
         *
         * Empty rather than null while someone else's is in flight: the
         * panels read a dozen fields off it, and `{}` renders as zeroes where
         * null would need a guard at every one of them.
         */
        dbInfo() {
            if (this.isOwnProfile || !this.userID) {
                return {
                    ...this.$store.state.dbInfo,
                    username: this.$store.state.user.username || 'Player',
                    avatar: this.$store.state.user.avatar,
                    premium: this.premium === true,
                }
            }
            return this.viewed || {}
        },

        /** Drives the Premium tab, which is about *your* subscription. */
        isPremium() { return this.premium === true },

        /**
         * The applied background. Read straight from the saved selection
         * rather than held in component state — the picker writes through the
         * API and refetches, so there is one answer and the page can't drift
         * from what was actually saved.
         */
        currentTheme() {
            return themes[this.dbInfo.theme] || themes.default
        },

        pageStyles() { return pageStyles(this.currentTheme) },

        /**
         * What was just claimed, described for the celebration.
         *
         * Normalised here rather than inside the celebration because the
         * reward → icon/gem/wording mapping is shared with the reward track;
         * a second copy in the component is a second thing to keep in step.
         */
        celebrationRewards() {
            if (!this.lastClaim) return []
            return (this.lastClaim.rewards || []).map(reward => ({
                kind: rewardKind(reward),
                label: describeReward(reward),
                icon: rewardIcon(reward),
                gemRarity: gemRarity(reward),
                amount: rewardAmount(reward),
                unit: rewardUnit(reward),
            }))
        },
    },
    methods: {
        /** A tab's target, carrying whichever profile is being looked at. */
        tabTo(name) {
            return { name, params: { id: this.$route.params.id || 'me' } }
        },

        /**
         * Fetches the viewed user when this isn't your own profile, and drops
         * any previously viewed one when it is.
         *
         * The clear matters: navigating from someone else's profile to your
         * own leaves `viewed` populated, and the moment `isOwnProfile` and a
         * stale `viewed` disagree the page renders a mix of the two.
         */
        /**
         * A full reload, not a re-render.
         *
         * By the time this is reachable a child has already thrown, so its
         * subtree is gone and the store may be half-populated. Re-entering
         * the route would run the same render against the same data and fail
         * identically; a reload is the only thing that genuinely starts over.
         */
        reloadProfile() {
            window.location.reload()
        },

        loadViewedProfile() {
            // `/profile/me` while logged out resolves to nobody, so there's
            // nothing to fetch and nothing to clear — the page renders its
            // signed-out shell.
            if (this.isOwnProfile || !this.userID) {
                this.$store.commit('profile/clearViewed')
                return
            }
            this.$store.dispatch('profile/fetchProfile', this.userID)
        },

        dismissClaim() { return this.$store.dispatch('profile/dismissClaim') },
    },
    watch: {
        /**
         * Re-resolve when the subject changes.
         *
         * Watching `userID` rather than `$route.params.id` covers both ways
         * it moves: navigating between profiles, and `/profile/me` finally
         * learning who "me" is when the signed-in user resolves after mount.
         */
        userID() {
            this.loadViewedProfile()
            // Customize doesn't exist on someone else's profile, and the
            // router's guard only runs on navigation — not on the subject
            // changing underneath a route we're already on.
            if (!this.isOwnProfile && this.$route.name === 'ProfileCustomize') {
                this.$router.replace(this.tabTo('ProfileOverview'))
            }
        },
        // Fires on mount too (immediate), which is when the saved theme
        // arrives from the store rather than at component creation.
        'currentTheme.font': {
            immediate: true,
            handler(family) { loadThemeFont(family) },
        },
    },
    /**
     * Catches a render or lifecycle failure in any panel below this shell.
     *
     * Vue's default for an uncaught render error is to tear the subtree down
     * and leave a blank page, with the reason only in a console nobody has
     * open — which is exactly how a profile "just stopped loading" with no
     * clue as to why.
     *
     * The shell survives, so the visitor gets an explanation and a way out
     * rather than nothing. Returning `false` stops the error propagating to
     * the global handler, since it has been handled here.
     *
     * Deliberately not a retry loop: whatever threw will throw again on the
     * same data, so the button reloads rather than re-rendering.
     *
     * Only catches what Vue routes through it — descendant render and
     * lifecycle errors. A rejected promise in an action still surfaces the
     * way it did before.
     */
    errorCaptured(err, vm, info) {
        // Reported before anything else: the console is the only place the
        // actual error exists, since the UI below shows generic copy.
        this.renderError = reportRenderError('profile', err, vm, info)
        // Vue's API for "handled — stop here". Without it the error carries
        // on to the global handler and the subtree is torn down anyway,
        // which is the blank page this exists to prevent.
        return false
    },
    beforeDestroy() {
        // Leaving must not leave someone else's profile in the store: the
        // next visit reads `viewed` before its own fetch resolves, and would
        // paint the previous user for a frame.
        this.$store.commit('profile/clearViewed')
    },
    async mounted() {
        // All public, so they load for logged-out visitors too.
        this.$store.dispatch('profile/fetchPremiumPrice')
        this.$store.dispatch('profile/fetchAchievements')
        this.$store.dispatch('profile/fetchQuestPool')
        this.$store.dispatch('profile/fetchGames')
        this.$store.dispatch('profile/fetchSeason')

        // Someone else's profile still loads from a public endpoint, so this
        // goes first and does not wait on the signed-in user's own data.
        // There is no longer a signed-out case to guard for: the router sends
        // a visitor without a token to /login before this component is
        // created (see router/profileGuard.js).
        this.loadViewedProfile()

        // dbInfo carries level, xp, stats and the cosmetic selections, so the
        // whole page depends on it — fetch it before the reward track, which
        // is rendered relative to the user's level.
        await this.$store.dispatch('fetchAllUserInfo')
        await Promise.all([
            this.$store.dispatch('profile/fetchRewards'),
            this.$store.dispatch('profile/fetchPremiumStatus'),
        ])

        // Returning from hosted Stripe Checkout. The webhook is what actually
        // grants premium, and it may not have landed yet — so this refetches
        // rather than assuming success, and says so if it hasn't arrived.
        if (this.$route.query.subscribed) {
            // Clear the flag first: it's served its purpose, and leaving it in
            // the URL means a refresh replays this whole thing.
            this.$router.replace({ query: {} })

            const granted = await this.$store.dispatch('profile/awaitPremium')
            this.$buefy.snackbar.open({
                message: granted
                    ? 'Welcome to Gamebot Premium! Your rewards are ready to claim.'
                    : "Payment received. Your perks are taking a moment to arrive — refresh shortly.",
                type: granted ? 'is-success' : 'is-warning',
                duration: granted ? 5000 : 8000,
            })

            if (granted) {
                // The premium half of the track just became claimable.
                await this.$store.dispatch('profile/fetchRewards')
            }
        }
    },
}
</script>
